import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { 
  Heart, Users, Star, Zap, MessageCircle, Building2, Wrench, 
  Globe, Map, Shield, Briefcase, Brain, Lightbulb, Target,
  Coffee, Code, Rocket, Layers, Activity, Phone, Mail,
  FileText, Database, Cloud, Sparkles, Compass, Send, Palette
} from 'lucide-react';

const projectsData = {
  en: {
    title: "Biig Projects",
    subtitle: "Everything we've built together. From AI-powered pousada management to global activity sharing.",
    footer: "Built with ❤️ for problem solvers and dreamers",
    projectCount: "projects and counting...",
    visit: "Visit →",
    categories: [
      {
        category: "Problem Discovery & AI Pousada",
        items: [
          {
            title: "Problem Explorer",
            description: "Complete journey through 36 questions about your pousada challenges",
            path: "/problems",
            icon: Heart,
            gradient: "from-pink-400 to-red-500"
          },
          {
            title: "Single Question Experience",
            description: "Send individual questions with stories, music, and reflection",
            path: "/questions",
            icon: Sparkles,
            gradient: "from-purple-400 to-pink-500"
          },
          {
            title: "Pousada Mastery",
            description: "AI-powered guest communication for beach pousadas",
            path: "/",
            icon: Building2,
            gradient: "from-blue-400 to-cyan-500"
          },
          {
            title: "Pousada Operations",
            description: "Operational tools and knolling system",
            path: "/pousada-ops",
            icon: Wrench,
            gradient: "from-green-400 to-teal-500"
          }
        ]
      },
      {
        category: "Location & Activity Sharing",
        items: [
          {
            title: "Location Pulse",
            description: "Share your location and activity with the world",
            path: "/share",
            icon: Globe,
            gradient: "from-blue-500 to-purple-500"
          },
          {
            title: "Global Activity Map",
            description: "See what people are doing around the world in real-time",
            path: "/global",
            icon: Map,
            gradient: "from-green-500 to-blue-500"
          },
          {
            title: "Moderation Dashboard",
            description: "Review and moderate shared activities",
            path: "/moderate",
            icon: Shield,
            gradient: "from-red-500 to-orange-500"
          }
        ]
      },
      {
        category: "Work & Productivity",
        items: [
          {
            title: "Work Buttons",
            description: "One-click workplace automation with habit tracking",
            path: "/work",
            icon: Briefcase,
            gradient: "from-amber-400 to-orange-500"
          },
          {
            title: "Flywheel Dashboard",
            description: "Business flywheel visualization and tracking",
            path: "/flywheel",
            icon: Activity,
            gradient: "from-indigo-500 to-purple-500"
          },
          {
            title: "Hidden Gems",
            description: "Discover hidden opportunities in your data",
            path: "/hidden-gems",
            icon: Star,
            gradient: "from-yellow-400 to-amber-500"
          },
          {
            title: "CTO Dashboard",
            description: "Tech operations, audit, and implementation roadmap",
            path: "/cto",
            icon: Target,
            gradient: "from-slate-600 to-blue-600"
          },
          {
            title: "Research Links",
            description: "Drop links, get AI-powered insights and expansions",
            path: "/research",
            icon: Brain,
            gradient: "from-indigo-600 to-purple-600"
          },
          {
            title: "AI Connection Test",
            description: "Test and debug Cloudflare AI bindings",
            path: "/ai-connection-test",
            icon: Zap,
            gradient: "from-purple-600 to-pink-600"
          }
        ]
      },
      {
        category: "Communication & Messaging",
        items: [
          {
            title: "WhatsApp Sender",
            description: "Bulk WhatsApp message automation",
            path: "/whatsapp",
            icon: MessageCircle,
            gradient: "from-green-400 to-green-600"
          },
          {
            title: "Telegram Business",
            description: "Business communication dashboard for Telegram",
            path: "/telegram-business",
            icon: Send,
            gradient: "from-blue-400 to-blue-600"
          },
          {
            title: "Executive Dashboard",
            description: "High-level communication metrics and insights",
            path: "/executive",
            icon: Target,
            gradient: "from-purple-500 to-indigo-500"
          }
        ]
      },
      {
        category: "AI & Mastery Tools",
        items: [
          {
            title: "Mobile Mastery",
            description: "Mobile-first AI experiences",
            path: "/mastery",
            icon: Phone,
            gradient: "from-cyan-400 to-blue-500"
          },
          {
            title: "Operator Mastery",
            description: "Tools for operation excellence",
            path: "/operator-mastery",
            icon: Brain,
            gradient: "from-purple-400 to-pink-500"
          },
          {
            title: "Unified Mastery",
            description: "Integrated mastery platform",
            path: "/unified",
            icon: Layers,
            gradient: "from-orange-400 to-red-500"
          }
        ]
      },
      {
        category: "Development & Infrastructure",
        items: [
          {
            title: "Dev Dashboard",
            description: "Development tools and monitoring",
            path: "/dev",
            icon: Code,
            gradient: "from-gray-600 to-gray-800"
          },
          {
            title: "Integration Forge",
            description: "Build and manage integrations",
            path: "/forge",
            icon: Zap,
            gradient: "from-red-500 to-yellow-500"
          },
          {
            title: "Adapter Manager",
            description: "Manage system adapters and connectors",
            path: "/adapters",
            icon: Database,
            gradient: "from-teal-500 to-cyan-500"
          },
          {
            title: "Architecture Diagram",
            description: "System architecture visualization",
            path: "/architecture",
            icon: Compass,
            gradient: "from-indigo-500 to-blue-600"
          },
          {
            title: "Project Structure",
            description: "Codebase structure and organization",
            path: "/structure",
            icon: FileText,
            gradient: "from-green-500 to-teal-600"
          }
        ]
      },
      {
        category: "Setup & Configuration",
        items: [
          {
            title: "Methodical Setup",
            description: "Step-by-step system configuration",
            path: "/setup",
            icon: Rocket,
            gradient: "from-blue-500 to-purple-600"
          },
          {
            title: "WhatsApp Setup Tool",
            description: "Configure WhatsApp integration",
            path: "/whatsapp-tool",
            icon: MessageCircle,
            gradient: "from-green-500 to-teal-500"
          },
          {
            title: "Telegram Setup Tool",
            description: "Configure Telegram integration",
            path: "/telegram-tool",
            icon: Send,
            gradient: "from-blue-500 to-cyan-500"
          }
        ]
      },
      {
        category: "Design & Branding",
        items: [
          {
            title: "Premium Color Palettes",
            description: "Exclusive luxury brand color schemes",
            path: "/colors",
            icon: Palette,
            gradient: "from-violet-500 to-fuchsia-500"
          }
        ]
      }
    ]
  },
  pt: {
    title: "Projetos Biig",
    subtitle: "Tudo que construímos juntos. Desde gestão de pousadas com IA até compartilhamento de atividades globais.",
    footer: "Feito com ❤️ para solucionadores de problemas e sonhadores",
    projectCount: "projetos e contando...",
    visit: "Visitar →",
    categories: [
      {
        category: "Descoberta de Problemas & IA Pousada",
        items: [
          {
            title: "Explorador de Problemas",
            description: "Jornada completa através de 36 perguntas sobre desafios da sua pousada",
            path: "/problems",
            icon: Heart,
            gradient: "from-pink-400 to-red-500"
          },
          {
            title: "Experiência de Pergunta Única",
            description: "Envie perguntas individuais com histórias, música e reflexão",
            path: "/questions",
            icon: Sparkles,
            gradient: "from-purple-400 to-pink-500"
          },
          {
            title: "Maestria Pousada",
            description: "Comunicação com hóspedes potencializada por IA para pousadas de praia",
            path: "/",
            icon: Building2,
            gradient: "from-blue-400 to-cyan-500"
          },
          {
            title: "Operações Pousada",
            description: "Ferramentas operacionais e sistema de organização",
            path: "/pousada-ops",
            icon: Wrench,
            gradient: "from-green-400 to-teal-500"
          }
        ]
      },
      {
        category: "Localização & Compartilhamento de Atividades",
        items: [
          {
            title: "Pulso de Localização",
            description: "Compartilhe sua localização e atividade com o mundo",
            path: "/share",
            icon: Globe,
            gradient: "from-blue-500 to-purple-500"
          },
          {
            title: "Mapa de Atividades Global",
            description: "Veja o que as pessoas estão fazendo ao redor do mundo em tempo real",
            path: "/global",
            icon: Map,
            gradient: "from-green-500 to-blue-500"
          },
          {
            title: "Painel de Moderação",
            description: "Revise e modere atividades compartilhadas",
            path: "/moderate",
            icon: Shield,
            gradient: "from-red-500 to-orange-500"
          }
        ]
      },
      {
        category: "Trabalho & Produtividade",
        items: [
          {
            title: "Botões de Trabalho",
            description: "Automação do local de trabalho com um clique e rastreamento de hábitos",
            path: "/work",
            icon: Briefcase,
            gradient: "from-amber-400 to-orange-500"
          },
          {
            title: "Painel Flywheel",
            description: "Visualização e rastreamento do flywheel de negócios",
            path: "/flywheel",
            icon: Activity,
            gradient: "from-indigo-500 to-purple-500"
          },
          {
            title: "Joias Escondidas",
            description: "Descubra oportunidades ocultas em seus dados",
            path: "/hidden-gems",
            icon: Star,
            gradient: "from-yellow-400 to-amber-500"
          },
          {
            title: "Painel CTO",
            description: "Operações técnicas, auditoria e roteiro de implementação",
            path: "/cto",
            icon: Target,
            gradient: "from-slate-600 to-blue-600"
          },
          {
            title: "Links de Pesquisa",
            description: "Adicione links, obtenha insights e expansões com IA",
            path: "/research",
            icon: Brain,
            gradient: "from-indigo-600 to-purple-600"
          }
        ]
      },
      {
        category: "Comunicação & Mensagens",
        items: [
          {
            title: "Enviador WhatsApp",
            description: "Automação de mensagens em massa do WhatsApp",
            path: "/whatsapp",
            icon: MessageCircle,
            gradient: "from-green-400 to-green-600"
          },
          {
            title: "Telegram Business",
            description: "Painel de comunicação empresarial para Telegram",
            path: "/telegram-business",
            icon: Send,
            gradient: "from-blue-400 to-blue-600"
          },
          {
            title: "Painel Executivo",
            description: "Métricas e insights de comunicação de alto nível",
            path: "/executive",
            icon: Target,
            gradient: "from-purple-500 to-indigo-500"
          }
        ]
      },
      {
        category: "IA & Ferramentas de Maestria",
        items: [
          {
            title: "Maestria Mobile",
            description: "Experiências de IA mobile-first",
            path: "/mastery",
            icon: Phone,
            gradient: "from-cyan-400 to-blue-500"
          },
          {
            title: "Maestria de Operador",
            description: "Ferramentas para excelência operacional",
            path: "/operator-mastery",
            icon: Brain,
            gradient: "from-purple-400 to-pink-500"
          },
          {
            title: "Maestria Unificada",
            description: "Plataforma de maestria integrada",
            path: "/unified",
            icon: Layers,
            gradient: "from-orange-400 to-red-500"
          }
        ]
      },
      {
        category: "Desenvolvimento & Infraestrutura",
        items: [
          {
            title: "Painel Dev",
            description: "Ferramentas de desenvolvimento e monitoramento",
            path: "/dev",
            icon: Code,
            gradient: "from-gray-600 to-gray-800"
          },
          {
            title: "Forja de Integração",
            description: "Construa e gerencie integrações",
            path: "/forge",
            icon: Zap,
            gradient: "from-red-500 to-yellow-500"
          },
          {
            title: "Gerenciador de Adaptadores",
            description: "Gerencie adaptadores e conectores do sistema",
            path: "/adapters",
            icon: Database,
            gradient: "from-teal-500 to-cyan-500"
          },
          {
            title: "Diagrama de Arquitetura",
            description: "Visualização da arquitetura do sistema",
            path: "/architecture",
            icon: Compass,
            gradient: "from-indigo-500 to-blue-600"
          },
          {
            title: "Estrutura do Projeto",
            description: "Estrutura e organização do código",
            path: "/structure",
            icon: FileText,
            gradient: "from-green-500 to-teal-600"
          }
        ]
      },
      {
        category: "Configuração & Setup",
        items: [
          {
            title: "Setup Metódico",
            description: "Configuração do sistema passo a passo",
            path: "/setup",
            icon: Rocket,
            gradient: "from-blue-500 to-purple-600"
          },
          {
            title: "Ferramenta Setup WhatsApp",
            description: "Configure integração com WhatsApp",
            path: "/whatsapp-tool",
            icon: MessageCircle,
            gradient: "from-green-500 to-teal-500"
          },
          {
            title: "Ferramenta Setup Telegram",
            description: "Configure integração com Telegram",
            path: "/telegram-tool",
            icon: Send,
            gradient: "from-blue-500 to-cyan-500"
          }
        ]
      },
      {
        category: "Design & Marca",
        items: [
          {
            title: "Paletas de Cores Premium",
            description: "Esquemas de cores exclusivos de luxo",
            path: "/colors",
            icon: Palette,
            gradient: "from-violet-500 to-fuchsia-500"
          }
        ]
      }
    ]
  }
};

export default function ProjectsDirectory() {
  const [language, setLanguage] = useState<'en' | 'pt'>('pt');
  const content = projectsData[language];

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-100 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Language selector */}
        <div className="flex justify-end mb-6 gap-2">
          <button
            onClick={() => setLanguage('en')}
            className={`px-4 py-2 rounded-lg font-medium transition-all ${
              language === 'en' 
                ? 'bg-blue-500 text-white shadow-lg' 
                : 'bg-white text-gray-600 hover:bg-gray-100'
            }`}
          >
            🇺🇸 English
          </button>
          <button
            onClick={() => setLanguage('pt')}
            className={`px-4 py-2 rounded-lg font-medium transition-all ${
              language === 'pt' 
                ? 'bg-green-500 text-white shadow-lg' 
                : 'bg-white text-gray-600 hover:bg-gray-100'
            }`}
          >
            🇧🇷 Português
          </button>
        </div>

        <div className="text-center mb-12">
          <h1 className="text-5xl font-bold text-gray-800 mb-4">
            {content.title}
          </h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            {content.subtitle}
          </p>
        </div>

        <div className="space-y-12">
          {content.categories.map((category, categoryIdx) => (
            <div key={categoryIdx}>
              <h2 className="text-2xl font-bold text-gray-700 mb-6 border-b border-gray-200 pb-2">
                {category.category}
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {category.items.map((project, idx) => {
                  const IconComponent = project.icon;
                  return (
                    <Link
                      key={idx}
                      to={project.path}
                      className="group bg-white rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300 p-6 border border-gray-100 hover:border-transparent"
                    >
                      <div className={`inline-flex p-3 rounded-xl bg-gradient-to-r ${project.gradient} mb-4 group-hover:scale-110 transition-transform`}>
                        <IconComponent className="text-white" size={24} />
                      </div>
                      
                      <h3 className="text-xl font-bold text-gray-800 mb-2">
                        {project.title}
                      </h3>
                      
                      <p className="text-gray-600 text-sm">
                        {project.description}
                      </p>
                      
                      <div className="mt-4 flex items-center text-sm text-gray-400">
                        <span className="group-hover:text-blue-500 transition-colors">
                          {content.visit}
                        </span>
                      </div>
                    </Link>
                  );
                })}
              </div>
            </div>
          ))}
        </div>

        <div className="mt-16 text-center border-t border-gray-200 pt-8">
          <p className="text-gray-500 text-sm">
            {content.footer}
          </p>
          <p className="text-gray-400 text-xs mt-2">
            {content.categories.reduce((acc, cat) => acc + cat.items.length, 0)} {content.projectCount}
          </p>
        </div>
      </div>
    </div>
  );
}